import { Component, EventEmitter, Input, Output } from '@angular/core';
import { PlatformCompanyAdminItem } from '../../shared/services/api.service';

@Component({
  selector: 'app-platform-company-admins-table',
  templateUrl: './platform-company-admins-table.component.html',
  styleUrls: ['./platform-company-admins-table.component.css'],
})
export class PlatformCompanyAdminsTableComponent {
  readonly invitePendingTooltip = 'Invite sent, account has not been set up yet';
  readonly deleteBlockedTooltip = 'Cannot delete the last active admin or billing owner';
  @Input() companyAdmins: PlatformCompanyAdminItem[] = [];
  @Input() loading = false;
  @Output() approve = new EventEmitter<string>();
  @Output() reject = new EventEmitter<string>();
  @Output() viewIdDocument = new EventEmitter<PlatformCompanyAdminItem>();
  @Output() resetPassword = new EventEmitter<PlatformCompanyAdminItem>();
  @Output() resendInvite = new EventEmitter<PlatformCompanyAdminItem>();
  @Output() delete = new EventEmitter<PlatformCompanyAdminItem>();
  displayedColumns: string[] = [
    'logo',
    'org',
    'email',
    'name',
    'billingOwner',
    'approvalStatus',
    'actions',
  ];

  get groupedCompanyAdmins(): PlatformCompanyAdminItem[] {
    return [...this.companyAdmins].sort((a, b) => {
      const orgCompare = (a.orgDisplayName || '').localeCompare(b.orgDisplayName || '', undefined, {
        sensitivity: 'base',
      });
      if (orgCompare !== 0) return orgCompare;

      const statusRankA = this.approvalStatusRank(a.approvalStatus);
      const statusRankB = this.approvalStatusRank(b.approvalStatus);
      if (statusRankA !== statusRankB) return statusRankA - statusRankB;

      const nameA = `${a.name || ''} ${a.surname || ''}`.trim();
      const nameB = `${b.name || ''} ${b.surname || ''}`.trim();
      const nameCompare = nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
      if (nameCompare !== 0) return nameCompare;

      return (a.email || '').localeCompare(b.email || '', undefined, { sensitivity: 'base' });
    });
  }

  approveMembership(membershipId: string): void {
    this.approve.emit(membershipId);
  }

  rejectMembership(membershipId: string): void {
    this.reject.emit(membershipId);
  }

  viewDocument(admin: PlatformCompanyAdminItem): void {
    this.viewIdDocument.emit(admin);
  }

  resetAdminPassword(admin: PlatformCompanyAdminItem): void {
    this.resetPassword.emit(admin);
  }

  resendAdminInvite(admin: PlatformCompanyAdminItem): void {
    this.resendInvite.emit(admin);
  }

  deleteAdmin(admin: PlatformCompanyAdminItem): void {
    this.delete.emit(admin);
  }

  isInvitePending(admin: PlatformCompanyAdminItem): boolean {
    return admin.approvalStatus === 'PENDING' && admin.invitePending === true;
  }

  isAwaitingReview(admin: PlatformCompanyAdminItem): boolean {
    return admin.approvalStatus === 'PENDING' && !this.isInvitePending(admin);
  }

  isDeleteBlocked(admin: PlatformCompanyAdminItem): boolean {
    if (admin.isBillingOwner === true) {
      return true;
    }
    if (admin.approvalStatus !== 'APPROVED') {
      return false;
    }
    const approvedAdminsInOrg = this.companyAdmins.filter(
      (item) => item.orgId === admin.orgId && item.approvalStatus === 'APPROVED'
    ).length;
    return approvedAdminsInOrg <= 1;
  }

  handleLogoError(event: Event): void {
    const img = event.target as HTMLImageElement | null;
    if (img) {
      img.style.display = 'none';
    }
  }

  isGroupStart(index: number): boolean {
    const list = this.groupedCompanyAdmins;
    if (index <= 0 || index >= list.length) return true;
    return list[index - 1].orgId !== list[index].orgId;
  }

  private approvalStatusRank(status: string | null | undefined): number {
    if (status === 'APPROVED') return 0;
    if (status === 'PENDING') return 1;
    return 2;
  }
}
